package org.gstechnovos.jexonplastech.sections.product_details.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionProperty
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

/**
 * ==========================================
 * IMAGE REQUIREMENTS: PRODUCT SHOWCASE & SLIDER
 * ==========================================
 * - Universal Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 800 x 800 pixels (Minimum 600 x 600 pixels).
 * - Composition: Product perfectly centered, taking up ~80% of the canvas (leave 10-20% margin for contain effects).
 * - Background/Format: Transparent or Pure White (#FFFFFF) in WebP or PNG format.
 * - Rendering Behavior:
 *     - Main Featured Image: Renders inside a max 600px wide by 400px high container using ObjectFit.Contain[cite: 47].
 *     - Thumbnails: Render inside rigid 80x80px square boxes with ObjectFit.Contain and 8px padding[cite: 47].
 */
@Composable
fun ProductShowcaseSection(
    images: List<String>,
    name: String,
    category: String,
    modelNumber: String
) {
    val palette = ColorMode.current.toSitePalette()
    var activeIndex by remember { mutableStateOf(0) }
    var isMainHovered by remember { mutableStateOf(false) }
    var isCtaHovered by remember { mutableStateOf(false) }

    val currentImage = images.getOrNull(activeIndex) ?: ""
    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.1)

    Div(
        attrs = Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
            .toAttrs {
                ref { element ->
                    visibilityRef(element)
                    onDispose {}
                }
            }
    ) {
        SimpleGrid(
            numColumns(1, md = 2),
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1200.px)
                .padding(leftRight = 5.percent, topBottom = 40.px)
                .gap(40.px)
                .animateSlideUp(isVisible, delayMs = 100)
        ) {
            // --- LEFT COLUMN: IMAGE SLIDER ---
            Column(modifier = Modifier.fillMaxWidth()) {
                // Main Featured Image Box with Zoom Effect
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(400.px)
                        .backgroundColor(palette.custom.lightGray)
                        .borderRadius(12.px)
                        .margin(bottom = 16.px)
                        .overflow(Overflow.Hidden)
                        .onMouseEnter { isMainHovered = true }
                        .onMouseLeave { isMainHovered = false },
                    contentAlignment = Alignment.Center
                ) {
                    if (currentImage.isNotEmpty()) {
                        Image(
                            src = currentImage,
                            alt = name,
                            modifier = Modifier
                                .fillMaxSize()
                                .objectFit(ObjectFit.Contain)
                                .padding(16.px)
                                .scale(if (isMainHovered) 1.08f else 1f)
                                .transition(Transition.of(TransitionProperty.All, duration = 400.ms))
                                .unextractable()
                        )
                    }
                }

                // Slider Thumbnails
                if (images.size > 1) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Start,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        images.forEachIndexed { index, imagePath ->
                            val isActive = index == activeIndex
                            var isThumbHovered by remember { mutableStateOf(false) }

                            Box(
                                modifier = Modifier
                                    .size(80.px)
                                    .margin(right = 16.px)
                                    .backgroundColor(palette.custom.lightGray)
                                    .borderRadius(10.px)
                                    .border(
                                        width = 2.px,
                                        style = LineStyle.Solid,
                                        color = if (isActive) palette.brand.primaryBlue else com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent
                                    )
                                    .scale(if (isThumbHovered || isActive) 1.05f else 1f)
                                    .cursor(Cursor.Pointer)
                                    .transition(Transition.of(TransitionProperty.All, duration = 200.ms))
                                    .onMouseEnter { isThumbHovered = true }
                                    .onMouseLeave { isThumbHovered = false }
                                    .onClick { activeIndex = index },
                                contentAlignment = Alignment.Center
                            ) {
                                Image(
                                    src = imagePath,
                                    alt = "Thumbnail $index",
                                    modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Contain).padding(8.px).unextractable()
                                )
                            }
                        }
                    }
                }
            }

            // --- RIGHT COLUMN: DETAILS & INTERACTIVE CTA ---
            Column(modifier = Modifier.fillMaxWidth().padding(topBottom = 20.px)) {
                SpanText(
                    text = category.uppercase(),
                    modifier = Modifier.color(palette.brand.primaryBlue).fontSize(14.px).fontWeight(FontWeight.Bold).margin(bottom = 8.px)
                )
                SpanText(
                    text = name,
                    modifier = Modifier.color(palette.custom.darkText).fontSize(32.px).fontWeight(FontWeight.Bold).margin(bottom = 8.px)
                )
                SpanText(
                    text = "SKU / Model: $modelNumber",
                    modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.6f)).fontSize(16.px).margin(bottom = 32.px)
                )

                Link(
                    path = "/contact?product=$modelNumber",
                    text = "Inquire About This Product",
                    modifier = Modifier
                        .backgroundColor(palette.brand.primaryBlue)
                        .color(palette.custom.white)
                        .padding(leftRight = 28.px, topBottom = 16.px)
                        .borderRadius(24.px)
                        .fontWeight(FontWeight.Bold)
                        .textDecorationLine(TextDecorationLine.None)
                        .translateY(if (isCtaHovered) (-3).px else 0.px)
                        .transition(Transition.of(TransitionProperty.All, duration = 300.ms))
                        .styleModifier {
                            if (isCtaHovered) {
                                property("box-shadow", "0 10px 20px rgba(0, 118, 255, 0.3)")
                            } else {
                                property("box-shadow", "0 4px 10px rgba(0, 118, 255, 0.15)")
                            }
                        }
                        .onMouseEnter { isCtaHovered = true }
                        .onMouseLeave { isCtaHovered = false }
                )
            }
        }
    }
}